// Build-time feature flags for the takeover wizard.
//
// CRA bakes `process.env.REACT_APP_*` at `react-scripts build` time, so
// flipping any of these requires an Amplify *redeploy* of the takeover-app
// — not just an env-var change on a running instance. This mirrors the
// existing `REACT_APP_DISABLE_*` kill-switch idiom used throughout App.jsx
// (e.g. REACT_APP_DISABLE_VERIFICATION_ENVELOPE, REACT_APP_DISABLE_FAILED_GATE).

// B-IDBADGE-TOGGLE (2026-05-29): master switch for the entire ID-badge
// sub-flow — Step09 (upsell "Earn an extra £5") + Step12 (verification
// "Waiting for you"). DEFAULT OFF.
//
// Why default off: the in-session verification leg traps sellers. Step12
// re-opens the seller's DICloak browser to look for the badge, but
// run_session_cleanup_task (auto-chained server-side right after the email
// change) has usually already released + wiped that profile, so the
// rescrape dies with B-NO-POOL-PICK and Step12 — which only advanced on a
// confirmed badge — left the seller stuck before bank/payment. Reference
// incident: Abdul Kazeem, session ce8f8e64 / LI-0219 (2026-05-29).
// Separately, the verified badge is added via LinkedIn's asynchronous
// phone/Persona passport review, which does not surface during the
// seller's session, so an in-session rescrape essentially never confirms.
//
// Set REACT_APP_ID_BADGE_ENABLED=true (+ redeploy) to re-enable the upsell.
// Even when enabled, Step12 no longer traps (it has an always-present
// "Continue" affordance) and the £5 bonus is held until the badge is
// actually verified (verified_badge_present === true) rather than merely
// claimed — the operator credits it out-of-band after confirming on
// LinkedIn.
export const ID_BADGE_ENABLED =
  process.env.REACT_APP_ID_BADGE_ENABLED === 'true';

// B-IDBADGE-V2 (2026-06-22): automated identity-verification path. When ON
// (default) AND ID_BADGE_ENABLED is on, the upsell drives LinkedIn's
// verification flow server-side, captures the QR code, and shows it to the
// seller (Step09 → initiate, Step12 → QR + verify/retry). When OFF, falls back
// to the legacy "open LinkedIn and add it yourself" copy (the pre-v2 behavior).
// Sub-gated under ID_BADGE_ENABLED, so nothing ships until BOTH are on. Flip
// OFF with REACT_APP_ID_VERIFICATION_AUTOMATED=false (+ Amplify redeploy).
export const ID_VERIFICATION_AUTOMATED =
  process.env.REACT_APP_ID_VERIFICATION_AUTOMATED !== 'false';

// B-VERIFICATION-OVERLAY kill switch. DEFAULT ON. When enabled (default),
// every challenge (code / two_factor / app_approval / captcha) is surfaced
// level-driven via the /state envelope + GenericChallenge. Set
// REACT_APP_DISABLE_VERIFICATION_ENVELOPE=1 (+ redeploy) to restore the legacy
// edge-triggered useInjection path verbatim.
//
// Single source of truth: App.jsx and Step06AutomatedLogin.jsx both import
// this — each previously defined its own copy of the expression, which could
// silently drift out of sync.
export const VERIFICATION_ENVELOPE_ENABLED =
  process.env.REACT_APP_DISABLE_VERIFICATION_ENVELOPE !== '1';

// B-RELEASE-ON-FAILURE (2026-06-13): kill switch for "let the seller cleanly
// retry a takeover that failed/parked". DEFAULT ON. When ON, a refresh / new
// tab PINS the seller to their session ONLY while the takeover is genuinely
// active-processing OR fully complete; for every other backend status
// (operator_review, failed, terminal_failed, recovering, recovering_password,
// waiting_for_seller_retry, cancelled, abandoned, or any unknown/future status)
// the persisted session is dropped and a clean Step-1 wizard is rendered so the
// seller retries immediately (a genuinely NEW session — new buyer email + fresh
// DICloak profile — because the idempotency key is cleared too, so the BE can't
// re-join the parked session). See utils/sessionDisposition.js for the
// pin/release taxonomy and docs/specs/takeover-release-on-failure.md.
//
// When OFF the wizard is byte-identical to the legacy always-resume behavior
// (resume on any non-404 snapshot; never auto-release). Flip OFF with
// REACT_APP_RELEASE_ON_FAILURE=false (also accepts 0/no/off) + an Amplify
// redeploy of takeover-app — instant rollback, no code revert.
export const RELEASE_ON_FAILURE_ENABLED =
  !['false', '0', 'no', 'off'].includes(
    String(process.env.REACT_APP_RELEASE_ON_FAILURE || '').trim().toLowerCase(),
  );

// RESET-CODE-INBOX-NUDGE (2026-10-09, thread reset-code-wizard-nudge): during a
// seller-initiated LinkedIn password reset the FE shows the informational
// RecoveringScreen, whose default copy is "You don't need to do anything. We'll
// continue automatically." That is actively wrong for the reset flow: LinkedIn
// emails a reset code to the SELLER's own inbox, so they DO need to go fetch it
// and keep this tab open — the code-entry prompt (waiting_for_code /
// GenericChallenge) surfaces moments later and is the thing they must act on.
//
// Measured (dossier 2026-10-09, section D): only 23/128 reset-code waits since
// 09-24 completed, and sellers who DO engage succeed fast (p50 95s from
// awaiting_2fa_at) — so the gap is "never started", i.e. sellers tab away during
// the passive wait, not the 300s cap (which stays unchanged, AC-5). When ON,
// RecoveringScreen's password-reset variant swaps the passive line for an active
// "watch your inbox for a reset code" nudge so the seller is primed to enter it
// the moment it arrives.
//
// DEFAULT OFF. Flag OFF is byte-identical to today's RecoveringScreen. Flip ON
// with REACT_APP_RESET_CODE_INBOX_NUDGE=true (+ an Amplify redeploy of
// takeover-app — CRA bakes REACT_APP_* at build time). Nothing changes live
// without the Meeting's press; a post-merge canary (AC-4) compares reset-code
// relay success against the 23/128 baseline before any widen.
export const RESET_CODE_INBOX_NUDGE_ENABLED =
  process.env.REACT_APP_RESET_CODE_INBOX_NUDGE === 'true';

// SNAP-ANDROID-SELFIE-HANDOFF (2026-11-03, thread takeover-completion-funnel):
// the Open-in-Chrome prompt on the selfie step for Snapchat's Android in-app
// browser (no file chooser there; 0/115 such sellers saved a selfie
// 10-20..11-03), and the Chrome tab's rejoin of the same session.
//
// DEFAULT OFF: Filip 11-03, "make sure the selfie fix is launched dormant
// because I'm not sure if that was the actual issue". Flag OFF is
// byte-identical to today: no prompt, and a ?handoff= link is ignored. Flip ON
// with REACT_APP_SNAP_CHROME_HANDOFF=true (+ an Amplify redeploy of
// takeover-app — CRA bakes REACT_APP_* at build time) on a press. The
// takeover-api half (createSession rejoinOnly) must be deployed first.
export const SNAP_CHROME_HANDOFF_ENABLED =
  process.env.REACT_APP_SNAP_CHROME_HANDOFF === 'true';
